import type { RefObject } from 'react';
import { useEffect } from 'react';

import {
  type SolutionId,
  trackSolutionContentSelection,
  trackSolutionSectionView,
} from './solutionAnalytics';
import {
  SOLUTION_CONTENT_SELECTOR,
  SOLUTION_SECTION_SELECTOR,
  readSolutionContentTarget,
} from './solutionAnalyticsAttributes';

/**
 * ### SolutionLandingAnalyticsOptions
 *
 * Identity every event from one landing page carries.
 */
export type SolutionLandingAnalyticsOptions = {
  /**
   * ### solutionId
   *
   * Solution reported as `solution_id` on every event of the page.
   */
  solutionId: SolutionId;

  /**
   * ### language
   *
   * Page language as routed, reported as `page_language` on section views.
   */
  language: string;
};

/**
 * ### useSolutionLandingAnalytics
 *
 * Wires a landing page's markup to the solution analytics events. Every
 * element under the root matching {@link SOLUTION_SECTION_SELECTOR} reports
 * one `view_solution_section` the first time its middle band enters the
 * viewport; every click on an element matching
 * {@link SOLUTION_CONTENT_SELECTOR} with a complete attribute set reports
 * `select_content`. Content tracked programmatically by its own handler must
 * not also carry the attributes, or it is reported twice.
 *
 * @param rootRef Element that bounds the observed sections and clicks
 * @param options Solution and language reported on every event
 */
export function useSolutionLandingAnalytics(
  rootRef: RefObject<HTMLElement | null>,
  { solutionId, language }: SolutionLandingAnalyticsOptions,
): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;

    const sections = Array.from(
      root.querySelectorAll<HTMLElement>(SOLUTION_SECTION_SELECTOR),
    );
    const seenSections = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const section = entry.target as HTMLElement;
          const sectionId = section.dataset.analyticsSection;
          if (!sectionId || seenSections.has(sectionId)) continue;

          seenSections.add(sectionId);
          trackSolutionSectionView(solutionId, {
            language,
            sectionId,
            sectionPosition: sections.indexOf(section) + 1,
          });
          observer.unobserve(section);
        }
      },
      { rootMargin: '-35% 0px -35% 0px' },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [language, rootRef, solutionId]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const content = target.closest<HTMLElement>(SOLUTION_CONTENT_SELECTOR);
      if (!content || !root.contains(content)) return;

      const selection = readSolutionContentTarget(content.dataset);
      if (!selection) return;

      trackSolutionContentSelection(solutionId, selection);
    };

    root.addEventListener('click', handleClick);
    return () => root.removeEventListener('click', handleClick);
  }, [rootRef, solutionId]);
}
